import { MouseEvent, useCallback, useState } from "react";
import { generatePath, Link, useSearchParams } from "react-router-dom";
import { Routes } from "router";
import { Typography } from "theme";
import { Chat } from "theme/icons";

import { Box, CircularProgress, Fade, Popper } from "@mui/material";
import { styled } from "@mui/system";

import { useBookingComments } from "../../../api/hooks/useBookingComments/useBookingComments.ts";
import { convertStingToDate } from "../../../utils/dateHelpers.ts";

const IconButton = styled("button")(({ theme }) => ({
  background: "none",
  border: "none",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(0.5),
  position: "relative",
  transition: "0.3s",
  width: "40px",
  height: "40px",

  "& svg": {
    width: "28px",
    height: "28px",
  },

  "&:hover": {
    opacity: 0.7,
  },
}));

const Badge = styled("span")(({ theme }) => ({
  position: "absolute",
  top: "-4px",
  right: "-4px",
  backgroundColor: theme.palette.error.main,
  color: "#fff",
  borderRadius: "50%",
  minWidth: "18px",
  height: "18px",
  fontSize: "10px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  lineHeight: 1,
  padding: "0 3px",
}));

const PopoverContent = styled(Box)(({ theme }) => ({
  borderWidth: 1,
  borderStyle: "dashed",
  borderColor: theme.palette.secondary.main,
  backgroundColor: theme.palette.primary.main,
  width: "320px",
  maxHeight: "480px",
  overflowY: "auto",
}));

const BookingComments = () => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const [searchParams] = useSearchParams();
  const selectedDate = convertStingToDate(searchParams.get("d")) ?? new Date();
  const dateParam = selectedDate.toISOString().split("T")[0];

  const { data, isFetching } = useBookingComments(dateParam);

  const handleClick = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((prev) => !prev);
  }, []);

  const canBeOpen = open && Boolean(anchorEl);
  const id = canBeOpen ? "comments-popper" : undefined;

  return (
    <>
      <IconButton aria-describedby={id} onClick={handleClick}>
        <Chat />
        {!!data?.length && <Badge>{data.length > 99 ? "99+" : data.length}</Badge>}
      </IconButton>

      <Popper id={id} open={open} anchorEl={anchorEl} transition placement="bottom-end">
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={350}>
            <PopoverContent>
              <Box px={2} pt={2} pb={1} borderBottom="1px solid rgba(0,0,0,0.12)">
                <Typography variant="h5" weight="semiBold">
                  Коментарі до бронювань
                </Typography>
              </Box>

              {isFetching && (
                <Box p={2} display="flex" justifyContent="center">
                  <CircularProgress color="secondary" size={24} />
                </Box>
              )}

              {!isFetching && !data?.length && (
                <Box p={2}>
                  <Typography variant="body2">Коментарів немає</Typography>
                </Box>
              )}

              {!isFetching && data?.map((item) => (
                <Box
                  key={item.bookingId}
                  px={2}
                  py={1.5}
                  style={{
                    borderBottomWidth: 1,
                    borderBottomStyle: "solid",
                    borderBottomColor: "rgba(0, 0, 0, 0.12)",
                  }}
                >
                  <Link to={generatePath(Routes.BookingDetails, { id: String(item.bookingId) })} onClick={() => setOpen(false)}>
                    <Typography variant="body2" color="primary">
                      Бронювання #{item.bookingId}
                    </Typography>
                  </Link>
                  <Typography variant="body1" weight="regular">
                    {item.comment}
                  </Typography>
                </Box>
              ))}
            </PopoverContent>
          </Fade>
        )}
      </Popper>
    </>
  );
};

export default BookingComments;

